import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Button,
  Input,
  Label,
  FormGroup,
  Badge,
  UncontrolledTooltip,
} from "reactstrap";
import {
  getTicket,
  updateTicket,
  closeTicket,
  deleteImpactedPart,
  getTicketConstants,
} from "./service";
import ImpactedPartsTable from "./ImpactedPartsTable";
import AddPartsModal from "./AddPartsModal";
import CutlistModal, { downloadCutlistCSV } from "./CutlistModal";

const TicketDetailPane = ({ ticketId, orderId, onClose, onUpdate }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    subject: "",
    analysis: "",
    eta: "",
    cost: "",
  });
  const [constants, setConstants] = useState(null);
  const [showAddPartsModal, setShowAddPartsModal] = useState(false);
  const [showCutlistModal, setShowCutlistModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ticketId) {
      loadTicket();
      loadConstants();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      setLoading(true);
      const response = await getTicket(ticketId);
      setTicket(response.ticket);
      setFormData({
        subject: response.ticket.subject || "",
        analysis: response.ticket.analysis || "",
        eta: response.ticket.eta ? response.ticket.eta.slice(0, 10) : "",
        cost: response.ticket.cost || "",
      });
      setError(null);
    } catch (err) {
      setError("Failed to load ticket");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadConstants = async () => {
    try {
      const response = await getTicketConstants();
      setConstants(response);
    } catch (err) {
      console.error("Failed to load constants", err);
    }
  };

  const handleFieldBlur = async (field) => {
    // Prevent clearing ETA when it already has a value
    if (field === "eta" && !formData.eta && ticket.eta) {
      // Reset formData to original value and exit edit mode
      setFormData({ ...formData, eta: ticket.eta.slice(0, 10) });
      setEditingField(null);
      return;
    }

    let hasChanged = false;

    if (field === "cost") {
      const formValue = formData.cost ? parseFloat(formData.cost) : null;
      const ticketValue = ticket.cost ? parseFloat(ticket.cost) : null;
      hasChanged = formValue !== ticketValue;
    } else if (field === "eta") {
      const formValue = formData.eta || "";
      const ticketValue = ticket.eta ? ticket.eta.slice(0, 10) : "";
      hasChanged = formValue !== ticketValue;
    } else {
      hasChanged = formData[field] !== ticket[field];
    }

    if (editingField === field && hasChanged) {
      try {
        const payload = { [field]: formData[field] };
        const response = await updateTicket(ticketId, payload);
        setTicket(response.ticket);
        // Update local state only, don't reload base page
      } catch (err) {
        setError(`Failed to update ${field}`);
        console.error(err);
      }
    }
    setEditingField(null);
  };

  const handleCloseTicket = async () => {
    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      setError("Cost is required and must be greater than 0");
      return;
    }

    if (
      !window.confirm(
        "Are you sure you want to close this ticket? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      const response = await closeTicket(ticketId, parseFloat(formData.cost));
      setTicket(response.ticket);
      // Update local state only, don't reload base page
      setError(null);
    } catch (err) {
      setError(
        err.response?.data?.errors?.base?.[0] || "Failed to close ticket"
      );
      console.error(err);
    }
  };

  const handlePartUpdate = () => {
    loadTicket();
    // Update local state only, don't reload base page
  };

  const handlePartDelete = async (partId) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this part from the ticket?"
      )
    ) {
      return;
    }

    try {
      await deleteImpactedPart(partId);
      loadTicket();
      // Update local state only, don't reload base page
    } catch (err) {
      setError("Failed to delete impacted part");
      console.error(err);
    }
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "danger",
      analysed: "warning",
      production: "info",
      closed: "success",
      invalid: "secondary",
    };
    return colors[status] || "secondary";
  };

  // Check if all parts are closed (delivered or invalid)
  const allPartsClosed =
    ticket?.impacted_parts && ticket.impacted_parts.length > 0
      ? ticket.impacted_parts.every(
          (part) => part.status === "delivered" || part.status === "invalid"
        )
      : false;

  // Show Close action if: can_close OR all parts are closed
  const canClose =
    (ticket?.can_close || allPartsClosed) &&
    ticket.status !== "closed" &&
    ticket.status !== "invalid";

  // Check if cost is set for closing
  const hasCost = formData.cost && parseFloat(formData.cost) > 0;

  return (
    <>
      <Offcanvas
        isOpen={!!ticketId}
        toggle={onClose}
        direction="end"
        style={{ width: "60%", top: "0", marginTop: 0 }}
        className="border-start position-fixed"
      >
        {!ticket ? (
          <>
            <OffcanvasHeader
              toggle={onClose}
              className="border-bottom pb-2 mb-0"
            >
              Loading Ticket...
            </OffcanvasHeader>
            <OffcanvasBody className="d-flex align-items-center justify-content-center">
              <div className="text-center">
                <i className="fas fa-spinner fa-spin fa-2x mb-3"></i>
                <p>Loading ticket details...</p>
              </div>
            </OffcanvasBody>
          </>
        ) : (
          <>
            <OffcanvasHeader
              toggle={onClose}
              className="border-bottom pb-2 mb-0"
            >
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">{ticket.identifier}</span>
                  {editingField === "subject" ? (
                    <Input
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      onBlur={() => handleFieldBlur("subject")}
                      autoFocus
                      style={{ fontSize: "1.25rem", fontWeight: "500" }}
                    />
                  ) : (
                    <span
                      onClick={() => setEditingField("subject")}
                      style={{ cursor: "pointer" }}
                    >
                      {ticket.subject}
                    </span>
                  )}
                </div>
              </div>
            </OffcanvasHeader>

            {/* Status and Cost Bar */}
            <div className="d-flex justify-content-between align-items-center bg-light px-3 py-2 border-bottom">
              <div className="d-flex align-items-center gap-3">
                <div>
                  <Label className="mb-1 small fw-bold">Status</Label>
                  <div>
                    <Badge
                      color={getStatusBadgeColor(ticket.status)}
                      className="text-uppercase"
                    >
                      {ticket.status}
                    </Badge>
                  </div>
                </div>

                <div>
                  <Label className="mb-1 small fw-bold">Cost (₹)</Label>
                  <div>
                    {editingField === "cost" ? (
                      <Input
                        type="number"
                        bsSize="sm"
                        value={formData.cost}
                        onChange={(e) =>
                          setFormData({ ...formData, cost: e.target.value })
                        }
                        onBlur={() => handleFieldBlur("cost")}
                        autoFocus
                        placeholder="Enter cost"
                        disabled={ticket.status === "closed"}
                        style={{ width: "120px" }}
                      />
                    ) : (
                      <span
                        onClick={() =>
                          ticket.status !== "closed" && setEditingField("cost")
                        }
                        style={{
                          cursor:
                            ticket.status === "closed" ? "default" : "pointer",
                          display: "inline-block",
                          minWidth: "120px",
                          padding: "4px 8px",
                        }}
                      >
                        {formData.cost ? `₹${formData.cost}` : "Click to set"}
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="mb-1 small fw-bold">ETA</Label>
                  <div>
                    {editingField === "eta" ? (
                      <Input
                        type="date"
                        bsSize="sm"
                        value={formData.eta}
                        onChange={(e) =>
                          setFormData({ ...formData, eta: e.target.value })
                        }
                        onBlur={() => handleFieldBlur("eta")}
                        autoFocus
                        disabled={ticket.status === "closed"}
                        style={{ width: "150px" }}
                      />
                    ) : (
                      <span
                        onClick={() =>
                          ticket.status !== "closed" && setEditingField("eta")
                        }
                        style={{
                          cursor:
                            ticket.status === "closed" ? "default" : "pointer",
                          display: "inline-block",
                          minWidth: "150px",
                          padding: "4px 8px",
                        }}
                      >
                        {formData.eta
                          ? (() => {
                              try {
                                const date = new Date(formData.eta);
                                return isNaN(date.getTime())
                                  ? formData.eta
                                  : date.toLocaleDateString();
                              } catch {
                                return formData.eta;
                              }
                            })()
                          : "Click to set"}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger mx-3 mt-3 mb-0" role="alert">
                {error}
                <button
                  type="button"
                  className="btn-close float-end"
                  onClick={() => setError(null)}
                ></button>
              </div>
            )}

            {ticket.status === "closed" && (
              <div className="alert alert-info mt-3">
                <i className="fas fa-info-circle me-2"></i>
                Ticket closed on{" "}
                {new Date(ticket.closed_on).toLocaleDateString()}
              </div>
            )}

            <OffcanvasBody className="p-3">
              {/* Impacted Parts Section */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  Impacted Parts ({ticket.impacted_parts?.length || 0})
                </h6>
                {ticket.status !== "closed" && ticket.status !== "invalid" && (
                  <div className="d-flex gap-2">
                    {ticket.impacted_parts && ticket.impacted_parts.length > 0 && (
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={() => setShowCutlistModal(true)}
                      >
                        <i className="fas fa-list me-2"></i>Edit Cutlist
                      </Button>
                    )}
                    {ticket.status === "production" && ticket.impacted_parts && ticket.impacted_parts.length > 0 && (
                      <Button
                        color="success"
                        size="sm"
                        onClick={() => downloadCutlistCSV(ticket.impacted_parts)}
                      >
                        <i className="fas fa-download me-2"></i>Download Cutlist
                      </Button>
                    )}

                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => setShowAddPartsModal(true)}
                    >
                      <i className="fa-solid fa-circle-plus me-2"></i>Add Parts
                    </Button>
                  </div>
                )}
              </div>

              <ImpactedPartsTable
                parts={ticket.impacted_parts || []}
                constants={constants}
                onUpdate={handlePartUpdate}
                onDelete={handlePartDelete}
                readOnly={
                  ticket.status === "closed" || ticket.status === "invalid"
                }
              />

              <hr />

              {/* Action Buttons */}
              <div className="d-flex gap-2 justify-content-end">
                {canClose && (
                  <span
                    id="close-ticket-wrapper"
                    style={{ display: "inline-block" }}
                  >
                    <Button
                      color="success"
                      onClick={handleCloseTicket}
                      disabled={!hasCost}
                    >
                      Close Ticket
                    </Button>
                    {!hasCost && (
                      <UncontrolledTooltip target="close-ticket-wrapper">
                        Cost is required to close the ticket
                      </UncontrolledTooltip>
                    )}
                  </span>
                )}
              </div>
            </OffcanvasBody>
          </>
        )}
      </Offcanvas>

      {showAddPartsModal && (
        <AddPartsModal
          isOpen={showAddPartsModal}
          toggle={() => setShowAddPartsModal(false)}
          ticketId={ticketId}
          orderId={orderId}
          onPartAdded={handlePartUpdate}
        />
      )}

      {showCutlistModal && (
        <CutlistModal
          isOpen={showCutlistModal}
          toggle={() => setShowCutlistModal(false)}
          parts={ticket?.impacted_parts || []}
          ticketStatus={ticket?.status}
          onUpdate={handlePartUpdate}
        />
      )}
    </>
  );
};

export default TicketDetailPane;
